import React, { useState } from "react";
import { FlaskConical } from "lucide-react";
import { testRunSchedule } from "../../../api/schedulesApi";
import { getNotificationErrorMessage, inspectNotificationTemplate } from "../../../api/whatsappNotificationsApi";
import { Alert, Field, btnSecondary, inputClass } from "../scheduling/ui";
import { LIMITS, configToForm, formToConfig } from "./notificationConfig";
import { MappingEditor, MappingList } from "./WhatsAppNotificationsPanel";

export const BASE_VARIABLES = ["customer.name", "recipient.name", "clinic.name"];
export const RECIPIENTS = [
  { value: "self_or_primary_contact", label: "Customer, else primary contact", hint: "Use the customer's WhatsApp ID. Only if absent, use their primary contact and the optional second template." },
  { value: "self_or_guardian", label: "Customer, else parent / guardian (legacy)", hint: "Legacy schedules use the primary or sole parent/guardian and reuse the direct template if no second template is configured." },
  { value: "self", label: "Customer only", hint: "Only the customer's own WhatsApp ID. Customers without one are skipped." },
];
const REASONS = {
  no_whatsapp_id: "No WhatsApp ID", no_parent_guardian_whatsapp_id: "No parent / guardian WhatsApp ID",
  ambiguous_contacts: "Ambiguous contacts", patient_dnd: "Customer on DND", recipient_dnd: "Recipient on DND",
  no_primary_contact: "No primary contact", ambiguous_primary_contacts: "Multiple primary contacts",
  no_primary_contact_whatsapp_id: "Primary contact has no WhatsApp ID",
  primary_contact_template_not_configured: "Primary-contact template not configured",
};

// Saved schedule config -> form state for the custom fields.
export function customToForm(config) {
  return {
    recipient: config?.recipient || "self_or_primary_contact",
    respect_dnd: config?.respect_dnd !== false,
    occurrence_mode: config?.occurrence_mode || "daily",
    template: configToForm({ ...(config?.template || {}), enabled: true }),
    primary_contact_template: config?.primary_contact_template
      ? configToForm({ ...config.primary_contact_template, enabled: true }) : null,
  };
}

// Form state -> API config. Returns [config, errors].
export function customFromForm(custom, toolId) {
  const errors = [];
  if (!toolId) errors.push("Choose a selector tool.");
  const convert = (form, label) => {
    if (!form.template_name.trim()) errors.push(`${label} template name is required.`);
    const { enabled, ...template } = formToConfig(form);
    [...template.parameter_mapping.header, ...template.parameter_mapping.body,
      ...template.parameter_mapping.buttons.flatMap((b) => b.parameters)].forEach((item) => {
      if (item.literal === "") errors.push(`${label}: Fixed text parameters cannot be empty.`);
      if (item.variable === "") errors.push(`${label}: Choose a variable for every parameter.`);
    });
    return template;
  };
  return [{
    recipient: custom.recipient, respect_dnd: custom.respect_dnd,
    occurrence_mode: custom.occurrence_mode || "daily",
    template: convert(custom.template, "Direct"),
    primary_contact_template: custom.primary_contact_template
      ? convert(custom.primary_contact_template, "Primary contact") : null,
  }, errors];
}

const variablesFrom = (columns, template) => {
  const pm = template.parameter_mapping;
  const used = [...pm.header, ...pm.body, ...pm.buttons.map((b) => b.parameter)]
    .filter((row) => row.kind === "variable" && row.value?.startsWith("row.")).map((row) => row.value);
  return [...new Set([...BASE_VARIABLES, ...columns.filter((c) => c !== "customer_id").map((c) => `row.${c}`), ...used])];
};

function TemplateFields({ botId, template, onChange, columns }) {
  const [message, setMessage] = useState({ kind: "", text: "" });
  const [busy, setBusy] = useState(false);
  const variables = variablesFrom(columns, template);
  const setTemplate = (patch) => onChange({ ...template, ...patch });
  const setMapping = (part) => (value) => setTemplate({ parameter_mapping: { ...template.parameter_mapping, [part]: value } });
  const buttons = template.parameter_mapping.buttons;

  const loadTemplate = async () => {
    setBusy(true); setMessage({ kind: "", text: "" });
    try {
      const shape = (await inspectNotificationTemplate(botId, {
        template_name: template.template_name.trim(), language_code: template.language_code.trim() || "en",
        phone_number_id: template.phone_number_id.trim() || null,
      })).mapping_shape;
      const slot = () => ({ kind: "variable", value: variables[0], url_prefix: "" });
      const pm = template.parameter_mapping;
      setTemplate({ parameter_mapping: {
        header: Array.from({ length: shape.header }, (_, i) => pm.header[i] || slot()),
        body: Array.from({ length: shape.body }, (_, i) => pm.body[i] || slot()),
        buttons: shape.buttons.map((index) => pm.buttons.find((b) => b.index === index) || { index, parameter: slot() }),
      } });
      setMessage({ kind: "info", text: "Approved template loaded. Map every slot. Nothing has been sent." });
    } catch (err) {
      setMessage({ kind: "error", text: getNotificationErrorMessage(err, "Could not load Meta template.") });
    } finally {
      setBusy(false);
    }
  };

  return <div className="space-y-3">
    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
      <Field label="Template name" hint="Approved Meta template.">
        <input aria-label="Template name" className={inputClass} value={template.template_name} onChange={(e) => setTemplate({ template_name: e.target.value })} />
      </Field>
      <Field label="Language code">
        <input aria-label="Language code" className={inputClass} value={template.language_code} onChange={(e) => setTemplate({ language_code: e.target.value })} />
      </Field>
      <Field label="Phone number ID" hint="Only when the bot has several numbers.">
        <input aria-label="Phone number ID" className={inputClass} value={template.phone_number_id} onChange={(e) => setTemplate({ phone_number_id: e.target.value })} />
      </Field>
    </div>
    <button type="button" className={btnSecondary} disabled={busy || !template.template_name.trim()} onClick={loadTemplate}>Load approved template</button>
    <Alert kind={message.kind || "error"}>{message.text}</Alert>
    <MappingList label="Header" rows={template.parameter_mapping.header} onChange={setMapping("header")} variables={variables} max={LIMITS.header} />
    <MappingList label="Body" rows={template.parameter_mapping.body} onChange={setMapping("body")} variables={variables} max={LIMITS.body} />
    {buttons.map((button, i) => <div key={button.index} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 p-2">
      <span className="text-xs font-bold text-slate-500">URL button {button.index}</span>
      <MappingEditor label={`Button ${button.index} parameter`} row={button.parameter} variables={variables}
        onChange={(next) => setMapping("buttons")(buttons.map((b, idx) => (idx === i ? { ...b, parameter: next } : b)))} />
    </div>)}
  </div>;
}

export default function CustomScheduleFields({ botId, tools, toolId, onToolChange, custom, onChange, timezone }) {
  const [columns, setColumns] = useState([]);
  const [extraColumn, setExtraColumn] = useState("");
  const [runDate, setRunDate] = useState("");
  const [result, setResult] = useState(null);
  const [message, setMessage] = useState({ kind: "", text: "" });
  const [busy, setBusy] = useState(false);
  const selected = RECIPIENTS.find((item) => item.value === custom.recipient);

  const testRun = async () => {
    setBusy(true); setResult(null); setMessage({ kind: "", text: "" });
    try {
      const data = await testRunSchedule(botId, {
        tool_id: toolId, recipient: custom.recipient, run_date: runDate || null, timezone: timezone || null,
        respect_dnd: custom.respect_dnd,
        occurrence_mode: custom.occurrence_mode || "daily",
        primary_contact_template_configured: Boolean(custom.primary_contact_template?.template_name.trim()),
      });
      setResult(data);
      setColumns(data.columns || []);
    } catch (err) {
      setMessage({ kind: "error", text: getNotificationErrorMessage(err, "Test run failed.") });
    } finally {
      setBusy(false);
    }
  };

  const addColumn = () => {
    const name = extraColumn.trim().toLowerCase();
    if (/^[a-z_][a-z0-9_]{0,62}$/.test(name)) { setColumns((c) => [...new Set([...c, name])]); setExtraColumn(""); }
  };

  return <div className="space-y-4 rounded-xl border border-slate-200 p-3">
    <Field label="Message occurrence" hint="Daily keeps existing behaviour. Selector key sends once per customer and returned occurrence_key within this schedule, even across different dates. Birthday selectors can return birthday:2026.">
      <select aria-label="Message occurrence" className={inputClass} value={custom.occurrence_mode || "daily"}
        onChange={(e) => { onChange({ ...custom, occurrence_mode: e.target.value }); setResult(null); }}>
        <option value="daily">Once per customer per day</option>
        <option value="selector">Once per customer per selector occurrence key</option>
      </select>
    </Field>
    <Field label="Who receives it (selector tool)" hint={'SQL tools from the Tools page whose metadata is {"usage": "schedule_selector"}. The query must return customer_id.'}>
      <select aria-label="Selector tool" className={inputClass} value={toolId || ""} onChange={(e) => { onToolChange(e.target.value || null); setResult(null); }}>
        <option value="">Choose a tool...</option>
        {tools.map((tool) => <option key={tool.id} value={tool.id}>{tool.name}</option>)}
      </select>
    </Field>
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={custom.respect_dnd}
        onChange={(e) => { onChange({ ...custom, respect_dnd: e.target.checked }); setResult(null); }} />
      Respect customer DND
    </label>
    {!custom.respect_dnd && <Alert kind="info">This schedule can message customers and primary contacts marked DND. WhatsApp restrictions and required consent still apply.</Alert>}
    {!tools.length && <p className="text-xs text-amber-700">No selector tools yet. Ask a super admin to create a SQL tool with metadata {'{"usage": "schedule_selector"}'}.</p>}
    <Field label="Send to" hint={selected?.hint}>
      <select aria-label="Send to" className={inputClass} value={custom.recipient} onChange={(e) => { onChange({ ...custom, recipient: e.target.value }); setResult(null); }}>
        {RECIPIENTS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
      </select>
    </Field>

    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Test run date"><input type="date" aria-label="Test run date" className={inputClass} value={runDate} onChange={(e) => setRunDate(e.target.value)} /></Field>
        <button type="button" className={btnSecondary} disabled={!toolId || busy} onClick={testRun}><FlaskConical size={14} /> Test run</button>
      </div>
      <p className="text-[11px] text-slate-400">Runs the selector read-only and shows who would be messaged. Nothing is saved or sent. Its columns become row.&lt;column&gt; variables.</p>
      {result && <div className="space-y-2 rounded-lg bg-slate-50 p-2 text-sm" aria-label="Test run result">
        <p><strong>{result.matched}</strong> customer(s) on {result.run_date}{result.truncated ? " (first 50 shown)" : ""}. Columns: {result.columns.join(", ") || "—"}</p>
        {result.rows.length > 0 && <table className="w-full text-left text-xs">
          <thead className="uppercase text-slate-500"><tr><th className="py-1">Customer</th><th>Recipient</th><th>Template variant</th><th>Result</th><th>Values</th></tr></thead>
          <tbody>{result.rows.map((row) => <tr key={row.customer_id} className="border-t border-slate-200">
            <td className="py-1">{row.customer_name || row.customer_id}</td>
            <td>{row.recipient_name || "—"}</td>
            <td>{row.template_variant === "primary_contact" ? "Primary contact" : row.template_variant === "direct" ? "Direct" : "—"}</td>
            <td>{row.status === "would_send" ? "Would send" : `Skip · ${REASONS[row.reason] || row.reason}`}</td>
            <td className="text-slate-500">{Object.entries(row.columns).filter(([k]) => k !== "customer_id").map(([k, v]) => `${k}=${v ?? ""}`).join(", ")}</td>
          </tr>)}</tbody>
        </table>}
      </div>}
    </div>

    <Alert kind={message.kind || "error"}>{message.text}</Alert>
    <div className="flex flex-wrap items-end gap-2">
      <Field label="Add a column variable" hint="If you have not run a test yet, type a column your query returns.">
        <input aria-label="Column name" className={inputClass} value={extraColumn} placeholder="e.g. years" onChange={(e) => setExtraColumn(e.target.value)} />
      </Field>
      <button type="button" className={btnSecondary} onClick={addColumn}>Add</button>
    </div>
    <fieldset aria-label="Direct message template" className="space-y-3 rounded-xl border border-slate-200 p-3">
      <legend className="text-sm font-semibold">Direct message template</legend>
      <TemplateFields botId={botId} template={custom.template} columns={columns}
        onChange={(template) => onChange({ ...custom, template })} />
    </fieldset>
    <label className="flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={Boolean(custom.primary_contact_template)}
        onChange={(e) => { onChange({ ...custom, primary_contact_template: e.target.checked
          ? configToForm({ enabled: true }) : null }); setResult(null); }} />
      Configure primary-contact template (optional)
    </label>
    <p className="text-xs text-slate-500">For messages about the customer sent to their primary contact. Without this template, these messages are skipped. Legacy guardian schedules retain their existing single-template behaviour.</p>
    {custom.primary_contact_template && <fieldset aria-label="Primary-contact message template" className="space-y-3 rounded-xl border border-slate-200 p-3">
      <legend className="text-sm font-semibold">Primary-contact message template</legend>
      <TemplateFields botId={botId} template={custom.primary_contact_template} columns={columns}
        onChange={(primary_contact_template) => onChange({ ...custom, primary_contact_template })} />
    </fieldset>}
  </div>;
}
